// Konfigurasi navigasi berbasis peran (RBAC) — menyesuaikan PRD bab 4.
// Setiap role melihat menu sidebar yang berbeda sesuai hak aksesnya.
import {
  LayoutDashboard, Users, Wallet, Package2, ShoppingBag, FileBarChart2,
  ShieldCheck, BadgeCheck, Percent, IdCard, ShoppingBasket, PiggyBank,
  TrendingUp, Package, Settings, Store, QrCode, LayoutTemplate, UserCog, MapPinned, Truck,
} from "lucide-react";

// Label tampilan untuk tiap role.
// Catatan: role INVESTOR dilebur ke PENGAWAS (satu peran pengawasan read-only).
export const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin",
  ADMIN_PUSAT: "Admin Pusat",
  ADMIN_WILAYAH: "Admin Wilayah",
  ADMIN_DAERAH: "Admin Daerah",
  ADMIN_CABANG: "Admin Cabang",
  ADMIN_RANTING: "Admin Ranting",
  ADMIN_GERAI: "Admin Gerai",
  ADMIN_KEUANGAN: "Admin Keuangan",
  ADMIN_KEUANGAN_WILAYAH: "Admin Keuangan Wilayah",
  ADMIN_KEUANGAN_DAERAH: "Admin Keuangan Daerah",
  ADMIN_KEUANGAN_CABANG: "Admin Keuangan Cabang",
  ADMIN_KEUANGAN_RANTING: "Admin Keuangan Ranting",
  ADMIN_KEUANGAN_GERAI: "Admin Keuangan Gerai",
  PENGAWAS: "Pengawas",
  MITRA_GERAI: "Mitra Gerai",
  ANGGOTA: "Anggota",
};

// Katalog seluruh menu dasbor manajemen (admin).
const ADMIN_ITEMS = {
  dashboard:  { to: "/admin",            label: "Dashboard", short: "Beranda",             icon: LayoutDashboard, end: true },
  anggota:    { to: "/admin/anggota",    label: "Manajemen Anggota", short: "Anggota",     icon: Users },
  keuangan:   { to: "/admin/keuangan",   label: "Kas & Keuangan", short: "Kas",        icon: Wallet },
  produk:     { to: "/admin/produk",     label: "Produk & Stok", short: "Produk",         icon: Package2 },
  pesanan:    { to: "/admin/pesanan",    label: "Kelola Pesanan", short: "Pesanan",        icon: ShoppingBag },
  pembayaran: { to: "/admin/pembayaran", label: "Verifikasi Pembayaran", short: "Verifikasi", icon: BadgeCheck },
  laporan:    { to: "/admin/laporan",    label: "Laporan & Analitik", short: "Laporan",    icon: FileBarChart2 },
  peran:      { to: "/admin/peran",      label: "Peran & RBAC", short: "Peran",          icon: ShieldCheck },
  bagihasil:  { to: "/admin/bagi-hasil", label: "Persentase Keuntungan", short: "Bagi Hasil", icon: Percent },
  shu:        { to: "/admin/shu",        label: "Pembagian Keuntungan", short: "SHU",  icon: PiggyBank },
  homepage:   { to: "/admin/homepage",   label: "Setting Homepage", short: "Homepage",      icon: LayoutTemplate },
  investor:   { to: "/admin/investor",   label: "Laporan Investor", short: "Investor",      icon: TrendingUp },
  pengurus:   { to: "/admin/pengurus",   label: "Persetujuan Pengurus", short: "Pengurus",  icon: UserCog },
  wilayah:    { to: "/admin/wilayah",    label: "Manajemen Wilayah", short: "Wilayah",     icon: MapPinned },
  pengadaan:  { to: "/admin/pengadaan",  label: "Pengadaan Barang", short: "Pengadaan",      icon: Truck },
};

// Peta role admin -> daftar kunci menu yang boleh diakses.
const ADMIN_MENU_BY_ROLE = {
  SUPER_ADMIN:   ["dashboard", "anggota", "keuangan", "produk", "pesanan", "pembayaran", "laporan", "peran", "bagihasil", "shu", "homepage", "investor", "pengurus", "wilayah", "pengadaan"],
  ADMIN_PUSAT:   ["dashboard", "anggota", "keuangan", "produk", "pesanan", "pembayaran", "laporan", "peran", "bagihasil", "shu", "investor", "pengurus", "wilayah", "pengadaan"],
  ADMIN_WILAYAH: ["dashboard", "anggota", "pesanan", "laporan", "pengurus", "wilayah", "pengadaan"],
  ADMIN_DAERAH:  ["dashboard", "anggota", "pesanan", "laporan", "pengurus", "wilayah", "pengadaan"],
  ADMIN_CABANG:  ["dashboard", "produk", "pesanan", "laporan", "pengurus", "wilayah", "pengadaan"],
  ADMIN_RANTING: ["dashboard", "anggota", "pembayaran", "laporan", "wilayah", "pengadaan"],
  ADMIN_GERAI:   ["dashboard", "produk", "pesanan", "laporan", "pengadaan"],
  ADMIN_KEUANGAN:["dashboard", "anggota", "keuangan", "pembayaran", "pengadaan"],
  // Admin keuangan per level wilayah — menu sama dengan keuangan pusat, data ter-scope wilayahnya.
  ADMIN_KEUANGAN_WILAYAH: ["dashboard", "anggota", "keuangan", "pembayaran", "pengadaan"],
  ADMIN_KEUANGAN_DAERAH:  ["dashboard", "anggota", "keuangan", "pembayaran", "pengadaan"],
  ADMIN_KEUANGAN_CABANG:  ["dashboard", "anggota", "keuangan", "pembayaran", "pengadaan"],
  ADMIN_KEUANGAN_RANTING: ["dashboard", "anggota", "keuangan", "pembayaran", "pengadaan"],
  ADMIN_KEUANGAN_GERAI:   ["dashboard", "anggota", "keuangan", "pembayaran", "pengadaan"],
  // PENGAWAS = gabungan Pengawas + Investor (read-only penuh, termasuk laporan investor)
  PENGAWAS:      ["dashboard", "keuangan", "laporan", "shu", "peran", "investor"],
};

// Katalog seluruh menu ruang anggota / penjual.
const MEMBER_ITEMS = {
  dashboard:  { to: "/anggota",            label: "Dasbor", short: "Beranda",         icon: LayoutDashboard, end: true },
  kartu:      { to: "/anggota/kartu",      label: "Kartu Anggota", short: "Kartu",  icon: IdCard },
  toko:       { to: "/anggota/toko",       label: "Toko Anggota", short: "Toko",   icon: ShoppingBasket },
  scan:       { to: "/anggota/scan",       label: "Scan QR Gerai", short: "Scan",  icon: QrCode, badge: "O2O" },
  jualan:     { to: "/anggota/jualan",     label: "Jualan Saya", short: "Jualan",    icon: Store, badge: "Penjual" },
  tabungan:   { to: "/anggota/tabungan",   label: "Buku Tabungan", short: "Tabungan",  icon: PiggyBank },
  shu:        { to: "/anggota/shu",        label: "SHU Tracker", short: "SHU",    icon: TrendingUp },
  pesanan:    { to: "/anggota/pesanan",    label: "Pesanan Saya", short: "Pesanan",   icon: Package },
  pengaturan: { to: "/anggota/pengaturan", label: "Pengaturan", short: "Akun",     icon: Settings },
};

// Peta role sisi-anggota -> daftar kunci menu.
const MEMBER_MENU_BY_ROLE = {
  ANGGOTA:     ["dashboard", "kartu", "toko", "scan", "jualan", "tabungan", "shu", "pesanan", "pengaturan"],
  MITRA_GERAI: ["dashboard", "jualan", "scan", "pesanan", "pengaturan"], // fokus penjual/gerai
};

// Tab navigasi bawah (mobile) ruang anggota — maksimal 4, sisanya lewat tombol "Menu".
const MEMBER_TABS_BY_ROLE = {
  ANGGOTA:     ["dashboard", "toko", "scan", "tabungan"],
  MITRA_GERAI: ["dashboard", "jualan", "scan", "pesanan"],
};

// Ambil menu admin sesuai role (default: hanya dashboard bila role tak dikenal).
export function adminMenuFor(role) {
  const keys = ADMIN_MENU_BY_ROLE[role] || ["dashboard"];
  return keys.map((k) => ADMIN_ITEMS[k]).filter(Boolean);
}

// Ambil menu ruang anggota sesuai role.
export function memberMenuFor(role) {
  const keys = MEMBER_MENU_BY_ROLE[role] || MEMBER_MENU_BY_ROLE.ANGGOTA;
  return keys.map((k) => MEMBER_ITEMS[k]).filter(Boolean);
}

// Role yang berhak masuk dasbor manajemen (/admin).
export const ADMIN_ROLES = Object.keys(ADMIN_MENU_BY_ROLE);
export const isAdminRole = (role) => ADMIN_ROLES.includes(role);

// Tab navigasi bawah ruang anggota sesuai role.
export function memberTabsFor(role) {
  const keys = MEMBER_TABS_BY_ROLE[role] || MEMBER_TABS_BY_ROLE.ANGGOTA;
  return keys.map((k) => MEMBER_ITEMS[k]).filter(Boolean);
}
